import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Box,
    LinearProgress,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Download as DownloadIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { downloadBulkWorkerPDFs } from '../../api/workersApi';

interface BulkDownloadModalProps {
    open: boolean;
    workerIds: string[];
    onClose: () => void;
    onSuccess?: () => void;
}

const BulkDownloadModal: React.FC<BulkDownloadModalProps> = ({
    open,
    workerIds,
    onClose,
    onSuccess,
}) => {
    const [includePersonalInfo, setIncludePersonalInfo] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    const handleDownload = async () => {
        setLoading(true);
        setError(null);
        setProgress(0);
        
        try {
            // Simulate progress for better UX since we can't track real progress
            const progressInterval = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 500);

            await downloadBulkWorkerPDFs(workerIds, includePersonalInfo);
            
            clearInterval(progressInterval);
            setProgress(100);
            
            // Brief delay to show 100% before closing
            setTimeout(() => {
                onSuccess?.();
                onClose();
                // Reset state after closing
                setTimeout(() => {
                    setProgress(0);
                    setError(null);
                }, 300);
            }, 500);
        } catch (err: any) {
            setError(err.message || 'Failed to generate CVs. Please try again.');
            setLoading(false);
            setProgress(0);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
            // Reset state after closing
            setTimeout(() => {
                setProgress(0);
                setError(null);
                setIncludePersonalInfo(true);
            }, 300);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={loading}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DownloadIcon color="primary" />
                    <Typography variant="h6">
                        Download {workerIds.length} Worker CV{workerIds.length !== 1 ? 's' : ''}
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Select the type of CVs to generate:
                    </Typography>
                    
                    <RadioGroup
                        value={includePersonalInfo}
                        onChange={(e) => setIncludePersonalInfo(e.target.value === 'true')}
                        sx={{ mb: 3 }}
                    >
                        <FormControlLabel
                            value={true}
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body1">
                                        Normal CVs
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Includes personal information (name, contact details)
                                    </Typography>
                                </Box>
                            }
                            disabled={loading}
                        />
                        <FormControlLabel
                            value={false}
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body1">
                                        Anonymous CVs
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Personal information removed for privacy
                                    </Typography>
                                </Box>
                            }
                            disabled={loading}
                        />
                    </RadioGroup>

                    {loading && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={progress}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {`${Math.round(progress)}%`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" align="center">
                                Generating {workerIds.length} CVs... This may take a moment.
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Alert severity="info" sx={{ mt: 2 }}>
                        The CVs will be downloaded as a single ZIP file.
                    </Alert>
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    startIcon={<CancelIcon />}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleDownload}
                    disabled={loading || workerIds.length === 0}
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                >
                    {loading ? 'Generating...' : `Download ${workerIds.length} CV${workerIds.length !== 1 ? 's' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkDownloadModal;